'use client'

import { Button, Cell, Divider, Dialog, Navigation, Text } from '@vinted/web-ui'
import { X24 } from '@vinted/monochrome-icons'
import { useIntl } from 'react-intl'

import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import {
  CurrencyAmountModel,
  formatCurrencyAmount,
} from '@marketplace-web/complaints/complaints-data'

const MS_PER_SECOND = 1000

type Props = {
  isOpen: boolean
  onClose: () => void
  discount?: {
    percent: number
    validFrom?: number
    validTo?: number
    note?: string
    showNote?: boolean
  }
  lowestPriceIn30Days?: CurrencyAmountModel
}

const ShippingDiscountModal = ({ isOpen, onClose, discount, lowestPriceIn30Days }: Props) => {
  const translate = useTranslate('checkout.shipping.delivery_details.discounts')
  const { formatDate, locale } = useIntl()

  const renderNote = () => {
    if (!discount?.note) return ''

    return discount.note
  }

  const formatPromotionPeriod = (): string => {
    if (!discount) return ''
    if (!discount.validFrom || !discount.validTo) return renderNote()

    const validFrom = formatDate(discount.validFrom * MS_PER_SECOND, {
      month: 'short',
      day: 'numeric',
    })
    const validTo = formatDate(discount.validTo * MS_PER_SECOND, {
      month: 'short',
      day: 'numeric',
    })

    return `${validFrom} - ${validTo}`
  }

  const formatPrice = () => {
    if (!lowestPriceIn30Days) return ''

    return formatCurrencyAmount(lowestPriceIn30Days, locale)
  }

  return (
    <Dialog show={isOpen} testId="discount-modal" closeOnOverlay defaultCallback={onClose}>
      <div className="u-fill-width">
        <Navigation
          right={
            <Button
              styling="flat"
              theme="amplified"
              iconName={X24}
              inline
              onClick={onClose}
              testId="shipping-discount-modal-close-x-button"
            />
          }
          body={
            <Text
              as="h2"
              type="title"
              width="parent"
              alignment="center"
              text={translate('modal_header')}
            />
          }
        />
        <Divider />
        <Cell
          title={<Text as="h2" text={translate('promotion_period_header')} type="title" />}
          body={<Text as="span" type="subtitle" text={formatPromotionPeriod()} />}
          testId="promotion-period"
          divider
        />
        {discount?.showNote && (
          <Cell
            title={<Text as="h2" text={translate('when_it_applies')} type="title" />}
            body={
              <Text
                as="span"
                type="subtitle"
                text={translate('discount_applies_vgo_first_time_incentive')}
              />
            }
            testId="vgo-first-time-incentive-note"
            divider
          />
        )}
        <Cell
          title={<Text as="h2" text={translate('lowest_price_in_30_days_header')} type="title" />}
          body={<Text as="span" type="subtitle" text={formatPrice()} />}
          testId="lowest-price-in-30-days"
        />
        <Cell>
          <Button
            text={translate('action.close')}
            styling="filled"
            onClick={onClose}
            testId="shipping-discount-modal-close-action"
          />
        </Cell>
      </div>
    </Dialog>
  )
}

export default ShippingDiscountModal
